const WEEKDAY_KEYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const PERMANENT_VALID_TO = "2099-12-31";

function toBoolean(value, defaultValue = false) {
  if (value === undefined || value === null || value === "") {
    return defaultValue;
  }

  if (typeof value === "boolean") {
    return value;
  }

  return String(value).trim().toLowerCase() === "true";
}

export function emptyWeekdayValues() {
  return WEEKDAY_KEYS.reduce((accumulator, key) => {
    accumulator[key] = "";
    return accumulator;
  }, {});
}

export function toWeekdayFormValues(source) {
  const baseValues = emptyWeekdayValues();

  WEEKDAY_KEYS.forEach((key) => {
    const value = source?.[key];
    baseValues[key] = value === null || typeof value === "undefined" ? "" : String(value);
  });

  return baseValues;
}

export function hasWeekdayOverrides(source = {}) {
  return WEEKDAY_KEYS.some((key) => String(source?.[key] ?? "").trim() !== "");
}

export function createRoomRateRow(seed = {}) {
  const weekdayMinValues = toWeekdayFormValues(seed.default_min_stay_by_weekday);
  const weekdayMaxValues = toWeekdayFormValues(seed.default_max_stay_by_weekday);
  const useWeekdayDefaults =
    typeof seed.use_stay_defaults_by_weekday === "boolean"
      ? seed.use_stay_defaults_by_weekday
      : hasWeekdayOverrides(weekdayMinValues) || hasWeekdayOverrides(weekdayMaxValues);

  return {
    id: seed.id ?? null,
    row_key: seed.row_key || `draft-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    onboarding_room_category_id: Number(seed.onboarding_room_category_id || 0),
    rate_code: seed.rate_code || "",
    name: seed.name || "",
    client_id: seed.client_id ?? "",
    group_key: seed.group_key || "",
    currency: seed.currency || "",
    valid_from: seed.valid_from ? String(seed.valid_from).slice(0, 10) : new Date().toISOString().slice(0, 10),
    valid_to: seed.valid_to ? String(seed.valid_to).slice(0, 10) : PERMANENT_VALID_TO,
    status: "active",
    adults: seed.adults ?? "",
    adults_match_type: seed.adults_match_type || "exact",
    children: seed.children ?? "",
    children_match_type: seed.children_match_type || "upto",
    max_occupancy: seed.max_occupancy ?? "",
    max_occupancy_match_type: seed.max_occupancy_match_type || "upto",
    is_refundable: toBoolean(seed.is_refundable, true),
    show_in_booking_engine: toBoolean(seed.show_in_booking_engine, true),
    cancellation_policy: seed.cancellation_policy || "",
    penalty_type: seed.penalty_type || "",
    penalty_amount: seed.penalty_amount ?? "",
    penalty_description: seed.penalty_description || "",
    min_price_amount: seed.min_price_amount ?? "",
    max_price_amount: seed.max_price_amount ?? "",
    default_min_stay: seed.default_min_stay ?? "",
    default_max_stay: seed.default_max_stay ?? "",
    use_stay_defaults_by_weekday: useWeekdayDefaults,
    default_min_stay_by_weekday: weekdayMinValues,
    default_max_stay_by_weekday: weekdayMaxValues,
  };
}

export function normalizeIncomingRoomRateRows(rows = []) {
  return rows.map((row) =>
    createRoomRateRow({
      ...row,
      row_key: `server-${row.id}`,
    })
  );
}

export function normalizeWeekdayPayload(source = {}) {
  const normalized = {};
  let hasAny = false;

  for (const key of WEEKDAY_KEYS) {
    const rawValue = source?.[key];

    if (rawValue === "" || rawValue === null || typeof rawValue === "undefined") {
      normalized[key] = null;
      continue;
    }

    const parsedValue = Number(rawValue);

    if (!Number.isInteger(parsedValue) || parsedValue < 1) {
      return { value: null, errorKey: key };
    }

    normalized[key] = parsedValue;
    hasAny = true;
  }

  return {
    value: hasAny ? normalized : null,
    errorKey: null,
  };
}

export function countWeekdayOverrides(source = {}) {
  return WEEKDAY_KEYS.reduce(
    (count, key) => count + (String(source?.[key] ?? "").trim() !== "" ? 1 : 0),
    0
  );
}

export function buildRoomRateRowsPayload(rows, { bookingEngineEnabled = true } = {}) {
  return rows.map((row) => {
    const useWeekdayDefaults = row.use_stay_defaults_by_weekday === true || row.use_stay_defaults_by_weekday === "true";
    const weekdayMinResult = normalizeWeekdayPayload(row.default_min_stay_by_weekday);
    const weekdayMaxResult = normalizeWeekdayPayload(row.default_max_stay_by_weekday);

    return {
      id: row.id || undefined,
      onboarding_room_category_id: row.onboarding_room_category_id,
      rate_code: String(row.rate_code || "").trim().toUpperCase(),
      name: String(row.name || "").trim(),
      client_id: row.client_id === "" ? null : Number(row.client_id),
      group_key: String(row.group_key || "").trim() || null,
      currency: String(row.currency || "").trim().toUpperCase() || null,
      valid_from: new Date().toISOString().slice(0, 10),
      valid_to: PERMANENT_VALID_TO,
      status: "active",
      adults: row.adults === "" ? null : Number(row.adults),
      adults_match_type: row.adults === "" ? null : row.adults_match_type || "exact",
      children: row.children === "" ? null : Number(row.children),
      children_match_type: row.children === "" ? null : row.children_match_type || "upto",
      max_occupancy: row.max_occupancy === "" ? null : Number(row.max_occupancy),
      max_occupancy_match_type:
        row.max_occupancy === "" ? null : row.max_occupancy_match_type || "upto",
      is_refundable: row.is_refundable === true || row.is_refundable === "true",
      show_in_booking_engine: bookingEngineEnabled
        ? row.show_in_booking_engine === true || row.show_in_booking_engine === "true"
        : false,
      cancellation_policy: String(row.cancellation_policy || "").trim() || null,
      penalty_type: String(row.penalty_type || "").trim() || null,
      penalty_amount: row.penalty_amount === "" ? null : Number(row.penalty_amount),
      penalty_description: String(row.penalty_description || "").trim() || null,
      min_price_amount: row.min_price_amount === "" ? null : Number(row.min_price_amount),
      max_price_amount: row.max_price_amount === "" ? null : Number(row.max_price_amount),
      default_min_stay: useWeekdayDefaults ? null : row.default_min_stay === "" ? null : Number(row.default_min_stay),
      default_max_stay: useWeekdayDefaults ? null : row.default_max_stay === "" ? null : Number(row.default_max_stay),
      default_min_stay_by_weekday: useWeekdayDefaults ? weekdayMinResult.value : null,
      default_max_stay_by_weekday: useWeekdayDefaults ? weekdayMaxResult.value : null,
    };
  });
}
